/* The lab's arithmetic: fills, overage, variant resolution and the weights
 * that end up on a batch sheet.
 *
 * Pure functions only — no Supabase — because this is where a silent error
 * costs real product. A variant that resolves to 99.4% makes a batch that is
 * wrong in a way nobody notices until it's in jars.
 */

// Bench rounding. Uniform decimals are unusable: two decimals on a 26 g
// weight is noise you have to read past, and zero decimals on a 0.94 g
// flavour oil charge is a different product.
// Absence is not zero. Number(null) and Number('') are both 0, and 0 g is a
// real weight — a blank container row must not read as a confident 0.00 g.
function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function roundGrams(value) {
  const n = toNumber(value);
  if (n === null) return null;
  const abs = Math.abs(n);
  const places = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return Number(n.toFixed(places));
}

// Always with the unit: a bare number on a bench sheet invites the wrong
// scale setting.
export function formatGrams(value) {
  const rounded = roundGrams(value);
  if (rounded === null) return '';
  const abs = Math.abs(rounded);
  const places = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return `${rounded.toFixed(places)} g`;
}

/* Batch sizes and fills, as opposed to ingredient weights.
 *
 * The spec's tiered rule ("≥ 100 g → 0 decimals") is about what you weigh:
 * two decimals on a 26 g charge is noise. But its own worked example prints
 * the batch size as 116.9 g, one decimal, in three separate places including
 * the acceptance criteria — and it's right to, because the batch size is a
 * derived total you compare against, not something you dial into a scale.
 * So totals keep a decimal until they're big enough for it to be noise.
 */
export function formatBatchG(value) {
  const n = toNumber(value);
  if (n === null) return '';
  return Math.abs(n) >= 1000 ? `${n.toFixed(0)} g` : `${n.toFixed(1)} g`;
}

export function formatPct(value) {
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(2)}%` : '';
}

// ---------------------------------------------------------------- fills

export function netFillG(containers) {
  if (!Array.isArray(containers)) return 0;
  return containers.reduce((sum, c) => {
    const grams = Number(c?.grams);
    const qty = Number(c?.qty);
    if (!Number.isFinite(grams) || !Number.isFinite(qty)) return sum;
    return sum + grams * qty;
  }, 0);
}

export function containerCount(containers) {
  if (!Array.isArray(containers)) return 0;
  return containers.reduce((sum, c) => {
    const qty = Number(c?.qty);
    return Number.isFinite(qty) ? sum + qty : sum;
  }, 0);
}

// What actually goes in the pot: the fill plus what stays on the spatula.
export function plannedBatchG(netFill, overagePct) {
  const net = Number(netFill);
  if (!Number.isFinite(net)) return 0;
  const over = Number(overagePct);
  return net * (1 + (Number.isFinite(over) ? over : 0) / 100);
}

// ----------------------------------------------------- variant resolution

export const RESOLUTION_ERROR = {
  NO_FLEX: 'no_flex',
  FLEX_NEGATIVE: 'flex_negative',
  OVER_CAP: 'over_cap',
};

const TOLERANCE = 0.0001;
const lineIngredientId = (line) => line.ingredient_id || null;

/* Resolve a version's lines against a variant.
 *
 *   1. start from the version's lines
 *   2. replace the percentage of every overridden ingredient
 *   3. give the remainder (100 − everything else) to the flex ingredient
 *
 * Step 3 is the whole trick, and it's how the XOXO line already works on
 * paper: sunflower oil absorbs the difference so every variant lands on
 * exactly 100% without restating the base.
 *
 * Returns { lines, total, error } — `error` is an object with a message
 * written for the person at the bench, never a thrown exception, because
 * every caller wants to show it rather than crash.
 */
export function resolveVariant({ lines, variant, flexIngredientId, ingredients = [] }) {
  const base = (lines || []).map((line) => ({ ...line, percentage: Number(line.percentage) || 0 }));
  const overrides = variant?.overrides || [];

  if (!overrides.length) {
    return { lines: base, total: sumPct(base), error: null };
  }

  const capOf = (ingredientId) => {
    const found = ingredients.find((i) => i.id === ingredientId);
    const cap = Number(found?.max_use_pct);
    return Number.isFinite(cap) ? { cap, name: found?.name } : null;
  };

  // A cap is a hard limit, not a warning: it's the reason the column exists.
  for (const override of overrides) {
    const limit = capOf(override.ingredient_id);
    const pct = Number(override.pct);
    if (limit && Number.isFinite(pct) && pct > limit.cap + TOLERANCE) {
      return {
        lines: base,
        total: sumPct(base),
        error: {
          kind: RESOLUTION_ERROR.OVER_CAP,
          message: `${limit.name || 'This ingredient'} is capped at ${limit.cap}% — ${variant.name} asks for ${pct}%.`,
        },
      };
    }
  }

  const applied = base.map((line) => {
    const override = overrides.find((o) => o.ingredient_id && o.ingredient_id === lineIngredientId(line));
    return override ? { ...line, percentage: Number(override.pct) || 0, overridden: true } : line;
  });

  if (!flexIngredientId) {
    return {
      lines: applied,
      total: sumPct(applied),
      error: {
        kind: RESOLUTION_ERROR.NO_FLEX,
        message:
          'This formula has variants but no flex ingredient, so there is nothing to absorb the difference. Nominate one in the formula editor.',
      },
    };
  }

  const flexIndex = applied.findIndex((line) => lineIngredientId(line) === flexIngredientId);
  if (flexIndex === -1) {
    return {
      lines: applied,
      total: sumPct(applied),
      error: {
        kind: RESOLUTION_ERROR.NO_FLEX,
        message: 'The flex ingredient is not in this version. Pick one of its own lines to flex.',
      },
    };
  }

  const others = applied.reduce((sum, line, i) => (i === flexIndex ? sum : sum + line.percentage), 0);
  const flexPct = round4(100 - others);

  if (flexPct < -TOLERANCE) {
    const flexLine = applied[flexIndex];
    return {
      lines: applied,
      total: sumPct(applied),
      error: {
        kind: RESOLUTION_ERROR.FLEX_NEGATIVE,
        message: `${flexLine.ingredient_name || 'The flex ingredient'} would go below 0%. Lower the override or adjust the base.`,
      },
    };
  }

  const resolved = applied.map((line, i) =>
    i === flexIndex ? { ...line, percentage: flexPct, flex: true } : line
  );
  return { lines: resolved, total: sumPct(resolved), error: null };
}

// Percentages carry four decimals internally so a flex line that lands on
// 4.0001 doesn't render as a total of 100.01%.
function round4(n) {
  return Math.round(n * 10000) / 10000;
}

export function sumPct(lines) {
  return round4((lines || []).reduce((sum, line) => sum + (Number(line.percentage) || 0), 0));
}

export function isBalanced(lines) {
  return Math.abs(sumPct(lines) - 100) < 0.005;
}

// ------------------------------------------------------- batch quantities

// Weights for the sheet. Rounded per line for the bench, but the total is
// the sum of the ROUNDED lines, not a separately rounded total — otherwise
// the column visibly doesn't add up to the figure printed beneath it.
export function batchQuantities(lines, plannedG) {
  const target = Number(plannedG) || 0;
  const rows = (lines || []).map((line) => {
    const grams = (Number(line.percentage) || 0) * target / 100;
    return { ...line, grams, gramsRounded: roundGrams(grams) };
  });
  const total = rows.reduce((sum, r) => sum + (r.gramsRounded || 0), 0);
  return { rows, total };
}

export function yieldPercent(actualUnits, plannedUnits) {
  const actual = Number(actualUnits);
  const planned = Number(plannedUnits);
  if (!Number.isFinite(actual) || !Number.isFinite(planned) || planned <= 0) return null;
  return Math.round((actual / planned) * 100);
}

// Expiry is computed at completion, never typed: it's on the label, and a
// hand-typed date is one someone eventually gets wrong.
export function expiryDate(completedOn, shelfLifeMonths) {
  const months = Number(shelfLifeMonths);
  if (!completedOn || !Number.isFinite(months) || months <= 0) return null;
  const [y, m, d] = String(completedOn).split('-').map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  const targetMonth = date.getMonth() + months;
  const end = new Date(date.getFullYear(), targetMonth + 1, 0);
  // Clamp rather than roll over: 31 Aug + 6 months is 28/29 Feb, not 3 March.
  date.setFullYear(date.getFullYear(), targetMonth, Math.min(d, end.getDate()));
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
